<template>
  <div class="page-wrap">
    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Members"
      subtitle="Manage all resident members of the society"
      add-label="Add"
      :show-view="true"
      :show-edit="true"
      :show-delete="true"
      @add="$router.push('/societyadmin/member/add')"
      @view="
        $router.push(
          '/societyadmin/member/view/' +
            $event.member_identity_id +
            '?unit_mapping_id=' +
            $event.unit_mapping_id,
        )
      "
      @edit="$router.push('/societyadmin/member/edit/' + $event.id)"
      @delete="confirmDelete($event.id)"
      :columns="columns"
      :items="members"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
    >
      <!-- Header extra: block filter + sort + export -->
      <template #header-extra>
        <div class="header-controls">
          <FilterDropdown
            v-if="useBlocks"
            v-model="selected_flat"
            :options="flats"
            item-text="name"
            item-value="id"
            placeholder="Filter by Block"
            @change="refreshTable"
          />

          <!-- Sort dropdown -->
          <v-menu offset-y bottom transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <button
                class="pt-sort-btn"
                v-bind="attrs"
                v-on="on"
                title="Sort members"
              >
                <v-icon size="15" color="#6B7280">mdi-sort</v-icon>
                <span class="pt-sort-label">{{ activeSortLabel }}</span>
                <v-icon size="14" color="#9CA3AF">mdi-chevron-down</v-icon>
              </button>
            </template>
            <v-list dense class="pt-sort-menu">
              <v-list-item
                v-for="opt in sortOptions"
                :key="opt.sortBy"
                :class="{ 'sort-active': sortBy === opt.sortBy }"
                @click="applySort(opt.sortBy)"
              >
                <v-list-item-title class="pt-sort-item-title">
                  <v-icon v-if="sortBy === opt.sortBy" size="13" :color="brandColor" class="mr-1">mdi-check</v-icon>
                  <span v-else style="display:inline-block;width:17px"></span>
                  {{ opt.label }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <div class="action-btn-wrap">
            <!-- ASC / DESC toggle -->
            <button
              class="pt-dir-btn"
              :title="sortDesc ? 'Descending — click for Ascending' : 'Ascending — click for Descending'"
              @click="toggleSortDir"
            >
              <v-icon size="18" :color="brandColor">
                {{ sortDesc ? 'mdi-sort-ascending' : 'mdi-sort-descending' }}
              </v-icon>
            </button>

            <button
              class="pt-export-btn"
              :style="{ background: brandColor }"
              @click="exportDialog = true"
              title="Export to Excel"
            >
              <v-icon size="16" color="white">mdi-download-outline</v-icon>
            </button>
          </div>
        </div>
      </template>

      <!-- Avatar column -->
      <template #col-profile_url="{ item }">
        <div class="mem-avatar-wrap">
          <img
            v-if="item.profile_url && item.profile_url !== 'null'"
            :src="API_BASE_URL + '/uploads/' + item.profile_url"
            :alt="item.name"
            class="mem-avatar-img mem-avatar-clickable"
            @click.stop="openPhoto(API_BASE_URL + '/uploads/' + item.profile_url, item.name)"
          />
          <div
            v-else
            class="mem-avatar-fallback"
            :style="{ background: primaryColor + '20', color: primaryColor }"
          >
            {{
              item.name && item.name !== "null"
                ? item.name.charAt(0).toUpperCase()
                : "U"
            }}
          </div>
          <div class="mem-name-wrap">
            <span class="mem-name">{{
              item.name && item.name !== "null" ? item.name : "—"
            }}</span>
            <span class="mem-type-badge" :class="getMemberTypeClass(item.member_type)">
              {{ getMemberTypeLabel(item.member_type) }}
            </span>
          </div>
        </div>
      </template>

      <!-- Unit number column -->
      <template #col-house_number="{ item }">
        <div class="house-number-badge">
           <v-icon
            size="14"
            color="#94A3B8"
            v-if="item.unit_type === '1'"
          >
            mdi-home-outline
          </v-icon>

          <v-icon
            size="14"
            color="#94A3B8"
            v-else-if="item.unit_type === '2'"
          >
            mdi-store-outline
          </v-icon>
          <span v-if="item.flat_name" class="flat-prefix"
            >{{ item.flat_name }}-</span
          >
          <span class="house-num">{{ item.house_number }}</span>
        </div>
      </template>

      <!-- Last active column -->
      <template #col-last_active_time="{ item }">
        <span class="date-text">{{
          item.last_active_time | formatDateTime
        }}</span>
      </template>
      <template #col-role="{ item }">
        <v-chip
          small
          label
          :color="item.role === 'primary' ? 'primary' : 'orange'"
          dark
          class="font-weight-bold text-uppercase"
          style="height: 20px; font-size: 10px"
        >
          {{ item.role }}
        </v-chip>
      </template>

      <!-- Mobile Number -->
      <template #col-mobile_no="{ item }">
        <div
          style="
            display: flex;
            align-items: center;
            font-size: 13px;
            font-weight: 600;
            color: var(--tx2);
          "
        >
          <v-icon size="14" color="#94A3B8" class="mr-1"
            >mdi-phone-outline</v-icon
          >
          <span class="phone-text"
            >{{
              item.country_code
                ? (item.country_code.toString().startsWith("+")
                    ? item.country_code
                    : "+" + item.country_code) + " "
                : ""
            }}{{ item.mobile_no || "—" }}</span
          >
        </div>
      </template>
    </PremiumTable>

    <!-- ── Confirm Delete Modal ─────────────── -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Member"
      message="Are you sure you want to delete the member and all their associated data?"
      confirm-text="Yes, Delete"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteHouse"
    />

    <!-- ── Export Confirmation ──────────────── -->
    <ConfirmModal
      v-model="exportDialog"
      type="primary"
      :btnColor="brandColor"
      title="Export to Excel"
      message="Are you sure you want to export the member list to an Excel file?"
      confirm-text="Yes, Export"
      @confirm="
        exportToExcel();
        exportDialog = false;
      "
    />

    <!-- ── Photo Viewer ──────────────────────── -->
    <MediaViewer
      v-model="mediaViewer.open"
      :items="mediaViewer.items"
      :index="0"
    />
  </div>
</template>

<script>
import { mapMutations, mapGetters, mapState } from "vuex";
import moment from "moment";
import PremiumTable from "../../common/PremiumTable.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import FilterDropdown from "../../common/FilterDropdown.vue";
import MediaViewer from "../../common/MediaViewer.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "HouseList",

  mixins: [filterMixin],

  components: { PremiumTable, ConfirmModal, FilterDropdown, MediaViewer },

  data() {
    return {
      mediaViewer: { open: false, items: [] },
      members: [],
      totalRecords: 0,
      loading: false,
      deleteLoading: false,
      deleteDialog: false,
      delete_id: null,
      flats: [],
      selected_flat: "",
      search: "",
      page: 1,
      itemsPerPage: 10,
      user: {},
      exportDialog: false,
      sortBy: "house_number",
      sortDesc: false,
      sortOptions: [
        { label: "Name",         sortBy: "name" },
        { label: "Unit",         sortBy: "house_number" },
        { label: "Last Active",  sortBy: "last_active_time" },
        // { label: "Date Added",   sortBy: "created_at" },
      ],
    };
  },

  computed: {
    ...mapGetters("society", ["useBlocks", "unitLabel", "unitType"]),
    ...mapState("theme", ["primaryColor", "buttonColor"]),
    brandColor() {
      return this.buttonColor || this.primaryColor || "#30384d";
    },
    API_BASE_URL: () => process.env.VUE_APP_API_URL,

    activeSortLabel() {
      const opt = this.sortOptions.find(o => o.sortBy === this.sortBy);
      return opt ? opt.label : "Sort";
    },

    columns() {
      return [
        { key: "profile_url", label: "Name", width: "250px", mobileWidth: "42%" },
        { key: "house_number", label: this.unitLabel || "Unit", width: "120px", mobileWidth: "28%" },
        // { key: "role", label: "Role" },
        { key: "mobile_no", label: "Contact No", hideOnMobile: true },
        { key: "last_active_time", label: "Last Active", hideOnMobile: true, hideOnTablet: true },
      ];
    },
  },

  watch: {
    page() {
      this.getMembers();
    },
    itemsPerPage() {
      this.getMembers();
    },
    search() {
      this.page = 1;
      this.getMembers();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    refreshTable() {
      this.page = 1;
      this.getMembers();
    },

    onSearch(val) {
      this.search = val;
    },

    applySort(sortBy) {
      this.sortBy = sortBy;
      this.page = 1;
      this.getMembers();
    },

    toggleSortDir() {
      this.sortDesc = !this.sortDesc;
      this.page = 1;
      this.getMembers();
    },

    getMembers() {
      this.loading = true;
      let flat_id = this.selected_flat;
      let unit_type = "";

      if (flat_id === "all_shops") {
        flat_id = "";
        unit_type = 2;
      }

      this.$axios
        .get("societyadmin/members", {
          params: {
            society_id: this.user.id,
            flat_id: flat_id || "",
            unit_type: unit_type,
            page: this.page,
            itemsPerPage: this.itemsPerPage,
            sortBy: this.sortBy,
            sortDesc: this.sortDesc,
            search: this.search,
          },
        })
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            this.members = data.members.data;
            this.totalRecords = data.members.total;
            if (data.members.page > data.members.lastPage) this.page = 1;
          } else {
            this.showError(data.message);
            if (data.type === "invalid") {
              localStorage.removeItem("user");
              this.$router.push("/societyadmin/login");
            }
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    confirmDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },

    deleteHouse() {
      this.deleteLoading = true;
      this.$axios
        .delete("societyadmin/members/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          const data = response.data;
          if (data.status) {
            this.deleteDialog = false;
            this.getMembers();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "2000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.deleteLoading = false;
        });
    },

    getFlats() {
      this.$axios.get("societyadmin/flats").then((response) => {
        const data = response.data;
        if (data.status) {
          let flats = [...data.flats];
          if (this.unitType === 3 && this.useBlocks) {
            flats.unshift({ id: "all_shops", name: "All Shops" });
          }
          this.flats = flats;
        }
      });
    },

    exportToExcel() {
      this.loading = true;
      this.$axios
        .get("societyadmin/export/members", {
          params: {
            society_id: this.user.id,
            flat_id: this.selected_flat,
            search: this.search,
          },
          responseType: "blob",
        })
        .then((response) => {
          this.loading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `Members_${moment().format("YYYY-MM-DD")}.xlsx`,
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(() => {
          this.loading = false;
          this.showError("Failed to export data");
        });
    },

    getFormattedDate(date) {
      return date ? moment(date).format("DD MMM, YYYY · hh:mm A") : "—";
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "2000",
        message: msg,
      });
    },

    getMemberTypeLabel(type) {
      if (type == 1) return 'Owner';
      if (type == 2) return 'Tenant';
      return 'Member';
    },

    getMemberTypeClass(type) {
      if (type == 1) return 'type-owner';
      if (type == 2) return 'type-tenant';
      return 'type-default';
    },

    openPhoto(url, name) {
      this.mediaViewer.items = [{ url, type: "image", name: name || "Photo" }];
      this.mediaViewer.open = true;
    },
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};
    this.getFlats();

    // Initialize persistent filters for this module
    this.initFilters("member");

    this.getMembers();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

@media (max-width: 600px) {
  .header-controls {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: stretch;
    width: 100%;
    gap: 8px;
  }

  .header-controls >>> .v-input,
  .header-controls >>> .fdd-wrap {
    width: 100% !important;
  }
  
  .header-controls >>> .fdd-trigger {
    width: 100%;
    max-width: 100%;
    justify-content: space-between;
  }
}

.action-btn-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ── Header extra controls ── */
.pt-icon-btn {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  border: 1px solid var(--border, #e2e8f0);
  background: var(--surface2, #f8fafc);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
}
.pt-icon-btn:hover {
  background: var(--surface3, #f1f5f9);
}

/* ── Avatar ── */
.mem-avatar-wrap {
  min-width: 0;
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
}
.mem-avatar-img {
  width: 34px;
  height: 34px;
  min-width: 34px;
  min-height: 34px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid var(--border, #e2e8f0);
}
.mem-avatar-clickable {
  cursor: pointer;
  transition: transform 0.18s, box-shadow 0.18s;
}
.mem-avatar-clickable:hover {
  transform: scale(1.12);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
.mem-avatar-fallback {
  width: 34px;
  height: 34px;
  min-width: 34px;
  min-height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}
.mem-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx, #111827);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mem-name-wrap {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.mem-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  width: fit-content;
}
.type-owner {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
  border: 1px solid rgba(16, 185, 129, 0.2);
}
.type-tenant {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
  border: 1px solid rgba(245, 158, 11, 0.2);
}
.type-default {
  background: rgba(100, 116, 139, 0.1);
  color: #64748b;
  border: 1px solid rgba(100, 116, 139, 0.2);
}

/* ── Other cell styles ── */
.house-number-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 8px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 12px;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}
.house-number-badge .house-num,
.house-number-badge .flat-prefix {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.house-number-badge .flat-prefix {
  color: var(--tx3, #6b7280);
}
.house-number-badge .house-num {
  color: var(--tx3, #6b7280);
}

.unit-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 11px;
  font-weight: 600;
  color: var(--tx2, #374151);
}
.phone-text,
.date-text {
  font-size: 12.5px;
  color: var(--tx2, #374151);
}

/* ── Sort button ── */
.pt-sort-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1.5px solid var(--border, #e2e8f0);
  background: var(--surface, #ffffff);
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.pt-sort-btn:hover {
  background: var(--surface2, #f8fafc);
  border-color: #cbd5e1;
}
.pt-sort-label {
flex: 1;
    text-align: left;
    font-size: 13px;
    font-weight: 700;
    color: var(--tx2, #374151);
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 0.2s;
}
.pt-dir-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1.5px solid var(--border, #e2e8f0);
  background: var(--surface, #ffffff);
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}
.pt-dir-btn:hover {
  background: var(--surface2, #f8fafc);
  border-color: #cbd5e1;
}
.pt-sort-menu {
  border-radius: 12px !important;
  padding: 6px !important;
  min-width: 170px;
}
.pt-sort-item-title {
  font-size: 13px !important;
  font-weight: 500;
  display: flex;
  align-items: center;
}
.sort-active {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 8px;
}
.sort-active .pt-sort-item-title {
  font-weight: 700;
}
</style>
